/**
 * GS-06 — Territory Manager / Regional Manager / Sales Director auto-fill from the selected
 * reps' reporting chain (owner decision 2026-10-01), not the partner account's territorial
 * chain.
 *
 * The chain is sales_positions.parent_position_id, walked upward from the Lead/Event Rep.
 * Levels are read from sales_positions.position_type. The live data is not uniform (QA has
 * 'Territory Manager' and 'Membership Sales Rep' rows next to 'territory_manager' /
 * 'sales_rep'), so types are normalised before matching. On PROD the hierarchy is expected
 * to arrive from Active Directory via the AD sync (manager links -> parent_position_id);
 * until then PROD reps have no parent, and the form falls back to the account chain.
 *
 * Decisions:
 *   - The lead's own position counts (a TM leading an event fills the TM slot with itself),
 *     matching derive_account_hierarchy, which also starts the walk at depth 0.
 *   - A PARTIAL chain fills only the levels it has; the missing ones are left blank rather
 *     than mixed with the account chain (an unrelated director next to the rep's TM would
 *     look authoritative and be wrong). The UI names the missing levels.
 *   - A lead with NO chain falls back to the account chain (all three levels) when there is
 *     one; otherwise the current values are kept.
 *   - Fields stay editable; this only proposes values.
 */

export interface ChainPosition {
  id: string;
  position_type: string | null;
  parent_position_id: string | null;
}

export interface ManagerLevels {
  tmId: string | null;
  rmId: string | null;
  sdId: string | null;
}

export const EMPTY_LEVELS: ManagerLevels = {
  tmId: null,
  rmId: null,
  sdId: null,
};

const TYPE_ALIASES: Record<string, string> = {
  sales_director: "director",
  membership_sales_rep: "sales_rep",
  regional: "regional_manager",
};

export function normalizePositionType(t: string | null | undefined): string {
  const k = (t ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return TYPE_ALIASES[k] ?? k;
}

const MAX_DEPTH = 10;

/** Walk upward from `startId` (inclusive). Cycle- and depth-safe. */
export function walkReportingChain(
  positions: readonly ChainPosition[],
  startId: string | null | undefined,
): ManagerLevels {
  const out: ManagerLevels = { ...EMPTY_LEVELS };
  if (!startId) {
    return out;
  }
  const byId = new Map(positions.map((p) => [p.id, p]));
  const seen = new Set<string>();
  let cur = byId.get(startId);
  for (let depth = 0; cur && depth <= MAX_DEPTH && !seen.has(cur.id); depth++) {
    seen.add(cur.id);
    const t = normalizePositionType(cur.position_type);
    if (t === "territory_manager" && !out.tmId) {
      out.tmId = cur.id;
    } else if (t === "regional_manager" && !out.rmId) {
      out.rmId = cur.id;
    } else if (t === "director" && !out.sdId) {
      out.sdId = cur.id;
    }
    cur = cur.parent_position_id ? byId.get(cur.parent_position_id) : undefined;
  }
  return out;
}

export function hasAnyLevel(l: ManagerLevels): boolean {
  return !!(l.tmId || l.rmId || l.sdId);
}

export function sameLevels(a: ManagerLevels, b: ManagerLevels): boolean {
  return a.tmId === b.tmId && a.rmId === b.rmId && a.sdId === b.sdId;
}

export interface LeadChainFill {
  levels: ManagerLevels;
  source: "rep" | "account" | "unchanged";
  /** labels of levels the lead's chain lacks (only when source = 'rep') */
  missing: string[];
}

/**
 * The three manager levels to propose for `leadId`.
 * `accountLevels` = the account chain (derive_account_hierarchy), when known.
 */
export function fillFromLeadChain(
  positions: readonly ChainPosition[],
  leadId: string | null | undefined,
  current: ManagerLevels,
  accountLevels: ManagerLevels | null,
): LeadChainFill {
  const chain = walkReportingChain(positions, leadId);
  if (hasAnyLevel(chain)) {
    const missing: string[] = [];
    if (!chain.tmId) {
      missing.push("Territory Manager");
    }
    if (!chain.rmId) {
      missing.push("Regional Manager");
    }
    if (!chain.sdId) {
      missing.push("Sales Director");
    }
    return { levels: chain, source: "rep", missing };
  }
  if (accountLevels && hasAnyLevel(accountLevels)) {
    return { levels: { ...accountLevels }, source: "account", missing: [] };
  }
  return { levels: { ...current }, source: "unchanged", missing: [] };
}

/** Other selected reps whose reporting chain differs from the lead's. */
export function repsWithDifferentChain(
  positions: readonly ChainPosition[],
  leadId: string | null | undefined,
  otherRepIds: readonly string[],
): string[] {
  if (!leadId) {
    return [];
  }
  const lead = walkReportingChain(positions, leadId);
  return Array.from(new Set(otherRepIds))
    .filter((id) => id && id !== leadId)
    .filter((id) => !sameLevels(walkReportingChain(positions, id), lead));
}
